// src/components/layout/AppHeader.tsx - Updated with French texts

import React from 'react';
import { Layout, Dropdown, Avatar, Badge, Space, Typography, Button, Modal, message } from 'antd';
import { 
  BellOutlined, 
  UserOutlined, 
  LogoutOutlined, 
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const { Header } = Layout;
const { Text } = Typography;

interface AppHeaderProps {
  collapsed: boolean;
  onMenuToggle: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({ collapsed, onMenuToggle }) => {
  const navigate = useNavigate();
  const { user, logout, userDisplayName } = useAuth();
  const [modalApi, contextHolder] = Modal.useModal();

  // Handle menu item clicks
  const handleMenuClick = async ({ key }: { key: string }) => {
    console.log('Clic de menu détecté ! Clé:', key);
    
    switch (key) {
      case 'profile':
        navigate('/profile');
        break;
        
      case 'settings':
        navigate('/settings');
        break;
        
      case 'logout':
        console.log('Déconnexion cliquée - affichage de la boîte de dialogue de confirmation');
        
        modalApi.confirm({
          icon: null,
          title: 'Confirmer la déconnexion',
          content: 'Êtes-vous sûr de vouloir vous déconnecter de votre compte ?',
          okText: 'Oui, se déconnecter',
          cancelText: 'Annuler',
          okType: 'danger',
          centered: true,
          onOk: async () => {
            console.log('Déconnexion confirmée par l\'utilisateur');
            try {
              console.log('Appel de la fonction de déconnexion...');
              await logout();
              console.log('Déconnexion terminée avec succès');
            } catch (error: any) {
              console.error('Erreur de déconnexion:', error);
              message.error(`Échec de la déconnexion : ${error.message}`);
            }
          },
        });
        break;
        
      default:
        break;
    }
  };

  // Create menu items using the new Ant Design v5 format
  const menuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Paramètres du profil',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Préférences',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Se déconnecter',
      danger: true,
    },
  ];

  return (
    <Header 
      style={{ 
        padding: '0 32px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid #d1fae5',
        height: '80px',
        boxShadow: '0 4px 6px rgba(34, 197, 94, 0.05)'
      }}
    >
      {/* Left Section */}
      <Space size="large" align="center">
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onMenuToggle}
          style={{
            fontSize: '20px',
            width: '52px',
            height: '52px',
            borderRadius: '16px',
            color: '#065f46',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)',
            border: '1px solid #d1fae5',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(34, 197, 94, 0.1)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #ecfdf5, #d1fae5)';
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(34, 197, 94, 0.15)';
            e.currentTarget.style.color = '#064e3b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #f0fdf4, #ecfdf5)';
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(34, 197, 94, 0.1)';
            e.currentTarget.style.color = '#065f46';
          }}
        />
      </Space>

      {/* Right Section */}
      <Space size="large" align="center">
        {/* Notifications with Jiigy styling */}
        <Badge count={3} size="small" style={{ backgroundColor: '#ef4444' }}>
          <Button
            type="text"
            icon={<BellOutlined />}
            style={{
              fontSize: '22px',
              width: '52px',
              height: '52px',
              borderRadius: '16px',
              color: '#065f46',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)',
              border: '1px solid #d1fae5',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 4px rgba(34, 197, 94, 0.1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #ecfdf5, #d1fae5)';
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(34, 197, 94, 0.15)';
              e.currentTarget.style.color = '#064e3b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #f0fdf4, #ecfdf5)';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(34, 197, 94, 0.1)';
              e.currentTarget.style.color = '#065f46';
            }}
            onClick={() => {
              message.info('Panneau de notifications bientôt disponible');
            }}
          />
        </Badge>

        {/* User Profile with enhanced Jiigy design */}
        <Dropdown
          menu={{
            items: menuItems,
            onClick: handleMenuClick,
            selectable: false
          }}
          trigger={['click']}
          placement="bottomRight"
          arrow={{ pointAtCenter: true }}
          overlayClassName="header-dropdown-menu">
          <div style={{ 
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            padding: '8px 20px',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)',
            border: '1px solid #d1fae5',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(34, 197, 94, 0.1)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #ecfdf5, #d1fae5)';
            e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(34, 197, 94, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #f0fdf4, #ecfdf5)';
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(34, 197, 94, 0.1)';
          }}
          >
            <Avatar 
              size={44}
              icon={<UserOutlined />}
              style={{
                background: 'linear-gradient(135deg, #4ade80, #22c55e)',
                marginRight: '12px',
                boxShadow: '0 2px 8px rgba(74, 222, 128, 0.3)',
                border: '2px solid #ffffff'
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <Text strong style={{ 
                color: '#064e3b', 
                fontSize: '15px',
                fontWeight: '600',
                letterSpacing: '-0.01em'
              }}>
                {userDisplayName || 'Administrateur'}
              </Text>
              <Text style={{ 
                color: '#047857', 
                fontSize: '13px',
                fontWeight: '500',
                opacity: 0.8
              }}>
                {user?.Staff?.role || user?.profile?.role || 'Administrateur'}
              </Text>
            </div>
          </div>
        </Dropdown>
        {contextHolder}
      </Space>
    </Header>
  );
};

export default AppHeader;